<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore, useMapGetter } from 'dashboard/composables/store';
import eachDayOfInterval from 'date-fns/eachDayOfInterval';
import endOfDay from 'date-fns/endOfDay';
import endOfMonth from 'date-fns/endOfMonth';
import format from 'date-fns/format';
import getUnixTime from 'date-fns/getUnixTime';
import parseISO from 'date-fns/parseISO';
import startOfDay from 'date-fns/startOfDay';
import startOfMonth from 'date-fns/startOfMonth';
import subDays from 'date-fns/subDays';
import subMonths from 'date-fns/subMonths';
import Report from 'dashboard/api/reports';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import MetricCard from '../components/overview/MetricCard.vue';
import TrafficHeatmap from './TrafficHeatmap.vue';
import AgentActivityCard from './AgentActivityCard.vue';

const props = defineProps({
  scope: { type: String, required: true },
});

const { t } = useI18n();
const store = useStore();
const allAgents = useMapGetter('agents/getAgents');
const currentAccountId = useMapGetter('getCurrentAccountId');

const RANGE_OPTIONS = [
  { key: 'today', labelKey: 'OVERVIEW_V2.RANGE.TODAY' },
  { key: 'yesterday', labelKey: 'OVERVIEW_V2.RANGE.YESTERDAY' },
  { key: 'last_7', labelKey: 'OVERVIEW_V2.RANGE.LAST_7_DAYS' },
  { key: 'last_14', labelKey: 'OVERVIEW_V2.RANGE.LAST_14_DAYS' },
  { key: 'last_30', labelKey: 'OVERVIEW_V2.RANGE.LAST_30_DAYS' },
  { key: 'this_month', labelKey: 'OVERVIEW_V2.RANGE.THIS_MONTH' },
  { key: 'last_month', labelKey: 'OVERVIEW_V2.RANGE.LAST_MONTH' },
  { key: 'custom', labelKey: 'OVERVIEW_V2.RANGE.CUSTOM' },
];
const MODES = [
  { key: 'all', labelKey: 'OVERVIEW_V2.TRAFFIC.MODE_ALL' },
  { key: 'incoming', labelKey: 'OVERVIEW_V2.TRAFFIC.MODE_INCOMING' },
  { key: 'outgoing', labelKey: 'OVERVIEW_V2.TRAFFIC.MODE_OUTGOING' },
];

// Today first — the owner opens this page to read the day in progress.
const rangeKey = ref('today');
const customSince = ref('');
const customUntil = ref('');
const inboxId = ref(0);
const mode = ref('all');
const includeAi = ref(true);
const report = ref(null);
const isLoading = ref(false);

const rangeBounds = computed(() => {
  const now = new Date();
  switch (rangeKey.value) {
    case 'today':
      return { since: startOfDay(now), until: now };
    case 'yesterday': {
      const day = subDays(now, 1);
      return { since: startOfDay(day), until: endOfDay(day) };
    }
    case 'last_7':
      return { since: startOfDay(subDays(now, 6)), until: now };
    case 'last_14':
      return { since: startOfDay(subDays(now, 13)), until: now };
    case 'last_30':
      return { since: startOfDay(subDays(now, 29)), until: now };
    case 'this_month':
      return { since: startOfMonth(now), until: now };
    case 'last_month': {
      const month = subMonths(now, 1);
      return { since: startOfMonth(month), until: endOfMonth(month) };
    }
    case 'custom': {
      if (!customSince.value || !customUntil.value) return null;
      const since = startOfDay(parseISO(customSince.value));
      const until = endOfDay(parseISO(customUntil.value));
      return since > until ? null : { since, until };
    }
    default:
      return null;
  }
});

const fetchReport = async () => {
  if (!rangeBounds.value) return;
  isLoading.value = true;
  try {
    const since = getUnixTime(rangeBounds.value.since);
    const until = getUnixTime(rangeBounds.value.until) + 1;
    const { data } = await Report.getOverviewV2({
      scope: props.scope,
      since,
      until,
      inboxIds: inboxId.value ? [inboxId.value] : undefined,
    });
    report.value = data;
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  store.dispatch('agents/get');
  fetchReport();
});

watch(
  () => props.scope,
  () => {
    inboxId.value = 0;
    fetchReport();
  }
);
watch([rangeBounds, inboxId], fetchReport);

const title = computed(() =>
  t(props.scope === 'mk' ? 'OVERVIEW_V2.HEADER_MK' : 'OVERVIEW_V2.HEADER_S3')
);
const inboxes = computed(() => report.value?.inboxes || []);

const daysList = computed(() => {
  if (!rangeBounds.value) return [];
  const now = new Date();
  const end = rangeBounds.value.until > now ? now : rangeBounds.value.until;
  return eachDayOfInterval({ start: rangeBounds.value.since, end }).map(day =>
    format(day, 'yyyy-MM-dd')
  );
});

const aiName = computed(() => {
  const aiAgent = (report.value?.agents || []).find(
    agent => agent.id === report.value?.ai_user_id
  );
  return aiAgent?.name || 'AI';
});

const trafficCells = computed(() => {
  const cells = {};
  (report.value?.heatmap || []).forEach(row => {
    const incoming = Number(row.incoming);
    const human = Number(row.human);
    const ai = Number(row.ai);
    const outgoing = human + (includeAi.value ? ai : 0);
    let value = incoming + outgoing;
    if (mode.value === 'incoming') value = incoming;
    if (mode.value === 'outgoing') value = outgoing;
    cells[`${row.d}|${row.h}`] = {
      value,
      tooltip: t('OVERVIEW_V2.TRAFFIC.TOOLTIP', { incoming, human, ai }),
    };
  });
  return cells;
});

const kpis = computed(() => {
  const totals = report.value?.totals || {};
  const previous = report.value?.previous_totals || {};
  const num = (source, key) => Number(source[key] || 0);
  const items = [
    {
      key: 'total',
      label: t('OVERVIEW_V2.KPI.TOTAL'),
      current: num(totals, 'human') + num(totals, 'ai'),
      previous: num(previous, 'human') + num(previous, 'ai'),
      effective: num(totals, 'effective_human') + num(totals, 'effective_ai'),
    },
    {
      key: 'human',
      label: t('OVERVIEW_V2.KPI.HUMAN'),
      current: num(totals, 'human'),
      previous: num(previous, 'human'),
      effective: num(totals, 'effective_human'),
    },
    {
      key: 'ai',
      label: t('OVERVIEW_V2.KPI.AI', { name: aiName.value }),
      current: num(totals, 'ai'),
      previous: num(previous, 'ai'),
      effective: num(totals, 'effective_ai'),
    },
    {
      key: 'incoming',
      label: t('OVERVIEW_V2.KPI.INCOMING'),
      current: num(totals, 'incoming'),
      previous: num(previous, 'incoming'),
    },
    {
      key: 'conversations',
      label: t('OVERVIEW_V2.KPI.CONVERSATIONS'),
      current: num(totals, 'conversations'),
      previous: num(previous, 'conversations'),
    },
  ];
  return items.map(item => ({
    ...item,
    delta: item.previous
      ? Math.round(((item.current - item.previous) / item.previous) * 100)
      : null,
  }));
});

const availabilityById = computed(() => {
  const map = {};
  (allAgents.value || []).forEach(agent => {
    map[agent.id] = agent.availability_status || 'offline';
  });
  return map;
});

const sumSeries = series =>
  (series || []).reduce((acc, [, , count]) => acc + Number(count), 0);

const agents = computed(() =>
  (report.value?.agents || [])
    .filter(agent => includeAi.value || !agent.ai)
    .map(agent => ({
      ...agent,
      availability: availabilityById.value[agent.id] || 'offline',
      rangeReplies: sumSeries(agent.replies),
      activityCount:
        sumSeries(agent.notes) +
        sumSeries(agent.labels) +
        sumSeries(agent.sends) +
        sumSeries(agent.drafts),
    }))
    .sort(
      (a, b) =>
        b.rangeReplies - a.rangeReplies || b.activityCount - a.activityCount
    )
);

const statusCounts = computed(() => {
  const counts = { online: 0, busy: 0, offline: 0 };
  agents.value.forEach(agent => {
    counts[agent.availability] = (counts[agent.availability] || 0) + 1;
  });
  return counts;
});

const activeAgents = computed(() =>
  agents.value.filter(agent => agent.availability !== 'offline')
);

// Drill-down: the replies behind a card, so a number can be read back to its work.
const messagesDialogRef = ref(null);
const selectedAgent = ref(null);
const agentMessages = ref([]);
const isLoadingMessages = ref(false);

const showAgentMessages = async agent => {
  if (!rangeBounds.value) return;
  selectedAgent.value = agent;
  agentMessages.value = [];
  isLoadingMessages.value = true;
  messagesDialogRef.value?.open();
  try {
    const { data } = await Report.getOverviewV2AgentMessages({
      scope: props.scope,
      since: getUnixTime(rangeBounds.value.since),
      until: getUnixTime(rangeBounds.value.until) + 1,
      inboxIds: inboxId.value ? [inboxId.value] : undefined,
      userId: agent.id,
      limit: 100,
    });
    agentMessages.value = data;
  } finally {
    isLoadingMessages.value = false;
  }
};

const conversationLink = displayId =>
  `/app/accounts/${currentAccountId.value}/conversations/${displayId}`;

const messageTime = value => format(new Date(value), 'd MMM · HH:mm');

// Attachment-only messages carry no text; say so instead of rendering a blank row.
const previewText = message =>
  message.preview || t('OVERVIEW_V2.MESSAGES.NO_TEXT');

const AVAILABILITY_DOT = {
  online: 'bg-n-teal-9',
  busy: 'bg-n-amber-9',
  offline: 'bg-n-slate-8',
};
</script>

<template>
  <div class="flex flex-col h-full gap-4 p-4 overflow-auto font-inter">
    <div>
      <h1 class="mb-0.5 text-xl font-medium text-n-slate-12">{{ title }}</h1>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('OVERVIEW_V2.SUBTITLE', { scope: props.scope.toUpperCase() }) }}
      </p>
    </div>

    <div class="flex flex-wrap items-center gap-2">
      <select
        v-model="rangeKey"
        class="h-9 px-2 pr-7 mb-0 text-sm rounded-lg outline outline-1 outline-n-container bg-n-solid-2 text-n-slate-12"
      >
        <option
          v-for="option in RANGE_OPTIONS"
          :key="option.key"
          :value="option.key"
        >
          {{ t(option.labelKey) }}
        </option>
      </select>
      <template v-if="rangeKey === 'custom'">
        <input
          v-model="customSince"
          type="date"
          :max="customUntil || undefined"
          class="h-9 px-2 mb-0 text-sm rounded-lg outline outline-1 outline-n-container bg-n-solid-2 text-n-slate-12"
        />
        <input
          v-model="customUntil"
          type="date"
          :min="customSince || undefined"
          class="h-9 px-2 mb-0 text-sm rounded-lg outline outline-1 outline-n-container bg-n-solid-2 text-n-slate-12"
        />
      </template>
      <select
        v-model.number="inboxId"
        class="h-9 px-2 pr-7 mb-0 text-sm rounded-lg outline outline-1 outline-n-container bg-n-solid-2 text-n-slate-12"
      >
        <option :value="0">
          {{ t('OVERVIEW_V2.ALL_INBOXES') }} ({{ inboxes.length }})
        </option>
        <option v-for="inbox in inboxes" :key="inbox.id" :value="inbox.id">
          {{ inbox.name }}
        </option>
      </select>
      <div class="flex-1" />
      <div
        class="flex rounded-lg p-0.5 outline outline-1 outline-n-container bg-n-solid-2"
      >
        <button
          v-for="option in MODES"
          :key="option.key"
          class="px-3 py-1 text-sm rounded-md"
          :class="
            mode === option.key
              ? 'bg-n-solid-3 text-n-slate-12'
              : 'text-n-slate-11'
          "
          @click="mode = option.key"
        >
          {{ t(option.labelKey) }}
        </button>
      </div>
      <button
        class="flex items-center h-9 gap-2 px-3 text-sm rounded-lg outline outline-1 outline-n-container bg-n-solid-2 text-n-slate-12"
        :aria-pressed="includeAi"
        @click="includeAi = !includeAi"
      >
        <span
          class="w-2.5 h-2.5 rounded-full"
          :class="includeAi ? 'bg-n-teal-9' : 'bg-n-slate-8'"
        />
        {{ t('OVERVIEW_V2.TRAFFIC.INCLUDE_AI', { name: aiName }) }}
      </button>
    </div>

    <MetricCard
      :header="t('OVERVIEW_V2.KPI.HEADER')"
      :is-loading="isLoading && !report"
      :loading-message="t('REPORT.LOADING_CHART')"
    >
      <div class="grid w-full grid-cols-2 gap-4 lg:grid-cols-5">
        <div v-for="kpi in kpis" :key="kpi.key">
          <div class="text-sm text-n-slate-11">{{ kpi.label }}</div>
          <div class="text-2xl font-medium text-n-slate-12">
            {{ kpi.current.toLocaleString() }}
          </div>
          <div
            v-if="kpi.effective !== undefined"
            class="text-xs text-n-teal-11"
            :title="t('OVERVIEW_V2.EFFECTIVE_HINT')"
          >
            {{
              t('OVERVIEW_V2.KPI.EFFECTIVE', {
                count: kpi.effective.toLocaleString(),
                rate: kpi.current
                  ? Math.round((kpi.effective / kpi.current) * 100)
                  : 0,
              })
            }}
          </div>
          <div
            v-if="kpi.delta !== null"
            class="text-xs"
            :class="kpi.delta >= 0 ? 'text-n-teal-11' : 'text-n-ruby-11'"
          >
            {{ kpi.delta >= 0 ? '+' : '' }}{{ kpi.delta }}%
            {{ t('OVERVIEW_V2.KPI.VS_PREVIOUS') }}
          </div>
        </div>
      </div>
    </MetricCard>

    <MetricCard :header="t('OVERVIEW_V2.AGENT_STATUS.HEADER')">
      <div class="flex flex-wrap items-center w-full gap-x-5 gap-y-2">
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <span class="w-2 h-2 rounded-full bg-n-teal-9" />
          <span class="font-medium text-n-slate-12">{{
            statusCounts.online
          }}</span>
          {{ t('OVERVIEW_V2.AGENT_STATUS.ONLINE') }}
        </div>
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <span class="w-2 h-2 rounded-full bg-n-amber-9" />
          <span class="font-medium text-n-slate-12">{{
            statusCounts.busy
          }}</span>
          {{ t('OVERVIEW_V2.AGENT_STATUS.BUSY') }}
        </div>
        <div class="flex items-center gap-2 text-sm text-n-slate-11">
          <span class="w-2 h-2 rounded-full bg-n-slate-8" />
          <span class="font-medium text-n-slate-12">{{
            statusCounts.offline
          }}</span>
          {{ t('OVERVIEW_V2.AGENT_STATUS.OFFLINE') }}
        </div>
        <div class="flex flex-wrap justify-end flex-1 gap-1.5">
          <span
            v-for="agent in activeAgents"
            :key="agent.id"
            class="flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs outline outline-1 outline-n-container bg-n-solid-3 text-n-slate-11"
          >
            {{ agent.name }}
            <span
              class="w-1.5 h-1.5 rounded-full"
              :class="AVAILABILITY_DOT[agent.availability]"
            />
          </span>
        </div>
      </div>
    </MetricCard>

    <MetricCard
      :header="t('OVERVIEW_V2.TRAFFIC.HEADER')"
      :is-loading="isLoading && !report"
      :loading-message="t('REPORT.LOADING_CHART')"
    >
      <TrafficHeatmap :days="daysList" :cells="trafficCells" />
    </MetricCard>

    <div>
      <h2 class="mb-0.5 text-lg font-medium text-n-slate-12">
        {{ t('OVERVIEW_V2.AGENTS.HEADER') }}
      </h2>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t('OVERVIEW_V2.AGENTS.HINT') }}
      </p>
    </div>
    <div class="grid grid-cols-1 gap-4 pb-8 xl:grid-cols-2">
      <AgentActivityCard
        v-for="agent in agents"
        :key="agent.id"
        :agent="agent"
        :days="daysList"
        @show-messages="showAgentMessages"
      />
    </div>

    <Dialog
      ref="messagesDialogRef"
      width="3xl"
      :title="
        t('OVERVIEW_V2.MESSAGES.TITLE', { name: selectedAgent?.name || '' })
      "
      :description="t('OVERVIEW_V2.MESSAGES.HINT')"
      :show-confirm-button="false"
      :cancel-button-label="t('OVERVIEW_V2.MESSAGES.CLOSE')"
    >
      <div class="flex flex-col max-h-[60vh] overflow-y-auto">
        <p v-if="isLoadingMessages" class="mb-0 text-sm text-n-slate-11">
          {{ t('REPORT.LOADING_CHART') }}
        </p>
        <p
          v-else-if="!agentMessages.length"
          class="mb-0 text-sm text-n-slate-11"
        >
          {{ t('OVERVIEW_V2.MESSAGES.EMPTY') }}
        </p>
        <a
          v-for="message in agentMessages"
          :key="message.id"
          :href="conversationLink(message.display_id)"
          target="_blank"
          rel="noopener noreferrer"
          class="grid items-start grid-cols-[8rem_1fr] gap-3 px-2 py-2 -mx-2 text-sm rounded-md sm:grid-cols-[8rem_11rem_1fr] hover:bg-n-alpha-1"
        >
          <span class="text-n-slate-11">{{
            messageTime(message.created_at)
          }}</span>
          <span class="hidden truncate sm:block text-n-slate-12">
            {{ message.contact_name }}
            <span class="block text-xs truncate text-n-slate-11">
              {{ message.phone_number || message.inbox_name }}
            </span>
          </span>
          <span class="min-w-0">
            <span
              class="line-clamp-2"
              :class="message.preview ? 'text-n-slate-12' : 'text-n-slate-11'"
            >
              {{ previewText(message) }}
            </span>
            <span v-if="!message.effective" class="text-xs text-n-slate-11">
              {{ t('OVERVIEW_V2.MESSAGES.NOT_EFFECTIVE') }}
            </span>
          </span>
        </a>
      </div>
    </Dialog>
  </div>
</template>
